import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Send, UserPlus, Sparkles, Loader2, ExternalLink, MapPin, ArrowLeft, MessageSquare,
  FileText, ImageIcon, Files as FilesIcon, Download,
} from "lucide-react";
import MessageAttachmentPicker from "@/components/MessageAttachmentPicker";
import MessageAttachmentList from "@/components/MessageAttachmentList";
import { resolveImageSrc } from "@/lib/api";

/**
 * EventMessagesPage — the organizer's single hub for everything that
 * happens around a specific event, post-creation:
 *
 *   Left column
 *     - Suggested vendors (same city + at least one category overlap;
 *       excludes anyone already invited, applied, or messaged)
 *     - Existing threads (grouped separately)
 *
 *   Right column
 *     - Active thread — organizer sends messages here
 *     - When no thread is selected, show the vendor's mini-card + the
 *       "Message" / "Invite" quick actions
 *
 * The layout intentionally mirrors an email inbox: the organizer picks a
 * vendor on the left, converses on the right. Keeps the mental model
 * simple even though "vendor matches", "invitations", and "messages" are
 * three distinct data sources on the backend.
 */
export default function EventMessagesPage() {
  const { eventId } = useParams();
  const { user, loading: authLoading } = useAuth();

  const [matches, setMatches] = useState([]);
  const [threads, setThreads] = useState([]);
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeVendorId, setActiveVendorId] = useState(null);
  // "messages" (default — inbox + threads) or "files" (attachment gallery).
  const [tab, setTab] = useState("messages");

  const reload = useCallback(async () => {
    try {
      const [ev, m, t] = await Promise.all([
        api.get(`/events/${eventId}`),
        api.get(`/events/${eventId}/vendor-matches`),
        api.get(`/events/${eventId}/threads`),
      ]);
      setEvent(ev.data);
      setMatches(m.data.items || []);
      setThreads(t.data.items || []);
    } catch (e) {
      toast.error("Could not load event messages");
    } finally { setLoading(false); }
  }, [eventId]);

  useEffect(() => {
    if (user?.role !== "organizer") return;
    reload();
  }, [user, reload]);

  if (authLoading) return null;
  if (!user) return <Navigate to={`/login?next=/organizer/events/${eventId}/messages`} replace />;
  if (user.role !== "organizer") return <Navigate to="/" replace />;

  if (loading) return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin" style={{ color: "var(--avt-purple)" }} />
    </div>
  );

  const activeThreadVendor = matches.find((m) => m.id === activeVendorId)
                          || threads.find((t) => t.vendor_id === activeVendorId);

  return (
    <div className="mx-auto max-w-6xl px-6 py-10" data-testid="event-messages-page">
      <div className="mb-6">
        <Link to="/organizer/dashboard"
              className="text-sm inline-flex items-center gap-1.5 underline"
              style={{ color: "var(--avt-purple)" }}
              data-testid="back-to-organizer-dashboard">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to my events
        </Link>
        <h1 className="font-serif text-4xl mt-3">{event?.title}</h1>
        <div className="text-sm mt-1" style={{ color: "var(--ink-soft)" }}>
          <MapPin className="h-3.5 w-3.5 inline mr-1" />
          {event?.location} · {event?.city} · {event?.date}
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {(event?.categories || (event?.category ? [event.category] : [])).map((c) => (
            <span key={c}
              className="px-2 py-0.5 text-[10px] uppercase tracking-widest rounded-full"
              style={{ backgroundColor: "var(--sand)", color: "var(--ink-soft)" }}
              data-testid={`event-category-${c}`}>
              {c}
            </span>
          ))}
        </div>
      </div>

      {/* Sub-tabs — Messages (default) vs Files. Files rolls up every PDF
          and image ever posted in any thread on this event, newest-first,
          so an organizer can pull up a contract weeks after the fact
          without hunting through DMs. */}
      <div className="flex items-center gap-1 border-b mb-5"
           style={{ borderColor: "var(--border-warm)" }}
           data-testid="event-messages-subtabs">
        <TabButton active={tab === "messages"} onClick={() => setTab("messages")}
                   icon={MessageSquare} label="Messages"
                   testId="subtab-messages" />
        <TabButton active={tab === "files"} onClick={() => setTab("files")}
                   icon={FilesIcon} label="Files"
                   testId="subtab-files" />
      </div>

      {tab === "files" ? (
        <FilesView eventId={eventId} />
      ) : (
      <div className="grid md:grid-cols-[320px_1fr] gap-5">
        {/* LEFT COLUMN */}
        <aside className="space-y-6" data-testid="event-messages-sidebar">
          {threads.length > 0 && (
            <ThreadsPanel threads={threads}
                          activeVendorId={activeVendorId}
                          onPick={setActiveVendorId} />
          )}
          <MatchesPanel matches={matches}
                        activeVendorId={activeVendorId}
                        onPick={setActiveVendorId}
                        eventId={eventId}
                        onChanged={reload} />
        </aside>

        {/* RIGHT COLUMN */}
        <section data-testid="event-messages-main">
          {activeVendorId ? (
            <ThreadPane
              eventId={eventId}
              vendorId={activeVendorId}
              vendorSummary={activeThreadVendor}
              onSent={reload}
            />
          ) : (
            <EmptySelection matches={matches} />
          )}
        </section>
      </div>
      )}
    </div>
  );
}

function TabButton({ active, onClick, icon: Icon, label, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className="px-4 py-2 text-sm font-semibold inline-flex items-center gap-2 transition-colors"
      style={{
        color: active ? "var(--avt-purple)" : "var(--ink-soft)",
        borderBottom: active ? "2px solid var(--avt-purple)" : "2px solid transparent",
      }}
    >
      <Icon className="h-3.5 w-3.5" /> {label}
    </button>
  );
}

function FilesView({ eventId }) {
  const [files, setFiles] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/events/${eventId}/files`);
        if (alive) setFiles(data.items || []);
      } catch {
        if (alive) setFiles([]);
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [eventId]);

  if (loading) return (
    <div className="text-center py-16">
      <Loader2 className="h-8 w-8 mx-auto animate-spin" style={{ color: "var(--avt-purple)" }} />
    </div>
  );
  if (!files || files.length === 0) return (
    <div className="card-warm rounded-2xl p-10 text-center" data-testid="files-empty">
      <FilesIcon className="h-10 w-10 mx-auto" style={{ color: "var(--avt-purple)" }} />
      <h2 className="mt-4 font-serif text-2xl">Nothing shared yet.</h2>
      <p className="mt-2 text-sm max-w-md mx-auto" style={{ color: "var(--ink-soft)" }}>
        Any PDF or image posted in any thread on this event will show up here — a running
        library so you can pull up a contract or venue photo weeks later.
      </p>
    </div>
  );

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="files-grid">
      {files.map((f, i) => (
        <FileCard key={`${f.message_id}-${i}`} file={f} />
      ))}
    </div>
  );
}

function FileCard({ file }) {
  const isImage = (file.content_type || "").startsWith("image/");
  const abs = resolveImageSrc(file.url);
  const sizeKb = file.size ? `${(file.size / 1024).toFixed(0)} KB` : "";
  const dateStr = file.sent_at ? new Date(file.sent_at).toLocaleDateString(undefined,
    { month: "short", day: "numeric", year: "numeric" }) : "";
  return (
    <div
      className="card-warm rounded-2xl p-3 flex flex-col"
      data-testid={`file-card-${file.message_id}`}
    >
      <a href={abs} target="_blank" rel="noopener noreferrer"
         className="block rounded-md overflow-hidden mb-3"
         style={{ backgroundColor: "var(--sand)", aspectRatio: "4 / 3" }}
         data-testid={isImage ? "file-image-preview" : "file-pdf-preview"}>
        {isImage ? (
          <img src={abs} alt={file.filename}
               className="w-full h-full"
               style={{ objectFit: "cover" }} />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center gap-2">
            <FileText className="h-10 w-10" style={{ color: "var(--avt-purple)" }} />
            <div className="text-[10px] uppercase tracking-widest font-semibold"
                 style={{ color: "var(--ink-soft)" }}>PDF</div>
          </div>
        )}
      </a>
      <div className="text-sm font-semibold truncate" title={file.filename}>{file.filename}</div>
      <div className="text-xs mt-1 flex items-center gap-2 flex-wrap"
           style={{ color: "var(--ink-soft)" }}>
        {sizeKb && <span>{sizeKb}</span>}
        {dateStr && <span>· {dateStr}</span>}
      </div>
      <div className="text-xs mt-1" style={{ color: "var(--ink-soft)" }}>
        {file.sender_role === "organizer" ? "You" : file.sender_name}{" → "}
        {file.sender_role === "organizer" ? file.vendor_name : "you"}
      </div>
      <div className="mt-3 flex gap-2">
        <a href={abs} target="_blank" rel="noopener noreferrer"
           className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-full text-xs border"
           style={{ borderColor: "var(--border-warm)" }}
           data-testid="file-open">
          <ExternalLink className="h-3 w-3" /> Open
        </a>
        <a href={abs} download={file.filename}
           className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs btn-primary"
           data-testid="file-download">
          <Download className="h-3 w-3" /> Save
        </a>
      </div>
    </div>
  );
}

function ThreadsPanel({ threads, activeVendorId, onPick }) {
  return (
    <div>
      <div className="overline mb-2" style={{ color: "var(--avt-purple)" }}>Active threads</div>
      <ul className="space-y-1" data-testid="threads-list">
        {threads.map((t) => (
          <li key={t.vendor_id}>
            <button
              onClick={() => onPick(t.vendor_id)}
              data-testid={`thread-${t.vendor_id}`}
              className="w-full text-left px-3 py-2 rounded-md transition-colors flex items-start gap-2"
              style={{
                backgroundColor: activeVendorId === t.vendor_id ? "var(--sand)" : "transparent",
              }}
            >
              <MessageSquare className="h-3.5 w-3.5 mt-1 shrink-0" style={{ color: "var(--avt-purple)" }} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{t.vendor_name}</div>
                <div className="text-xs truncate" style={{ color: "var(--ink-soft)" }}>
                  {t.last_message}
                </div>
              </div>
              {t.unread > 0 && (
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: "var(--avt-pink)", color: "#fff" }}>
                  {t.unread}
                </span>
              )}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function MatchesPanel({ matches, activeVendorId, onPick, eventId, onChanged }) {
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  // Reset selections when the match list changes underneath us (e.g. after
  // an invite fires and the matches reload without them).
  useEffect(() => {
    setSelected((prev) => {
      const alive = new Set();
      const ids = new Set(matches.map((m) => m.id));
      prev.forEach((id) => { if (ids.has(id)) alive.add(id); });
      return alive;
    });
  }, [matches]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(matches.map((m) => m.id)));
  const clearAll = () => setSelected(new Set());

  const bulkInvite = async () => {
    if (bulkBusy || selected.size === 0) return;
    setBulkBusy(true);
    // Fire all invites in parallel — the endpoint is idempotent so a
    // "double-tap" during a network stall stays safe.
    const ids = Array.from(selected);
    try {
      const results = await Promise.allSettled(
        ids.map((id) => api.post("/ai/invite-vendor",
          { event_id: eventId, vendor_id: id })),
      );
      const okCount = results.filter((r) => r.status === "fulfilled").length;
      const failCount = results.length - okCount;
      if (okCount > 0) {
        toast.success(`Invited ${okCount} vendor${okCount === 1 ? "" : "s"}${failCount ? ` (${failCount} failed)` : ""}`);
      }
      if (okCount === 0 && failCount > 0) {
        toast.error("None of the invites went through — please retry");
      }
      setSelected(new Set());
      onChanged?.();
    } finally { setBulkBusy(false); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="overline flex items-center gap-1.5" style={{ color: "var(--avt-teal)" }}>
          <Sparkles className="h-3 w-3" /> Suggested vendors
        </div>
        {matches.length > 0 && (
          <button
            onClick={selected.size === matches.length ? clearAll : selectAll}
            className="text-[11px] underline"
            style={{ color: "var(--ink-soft)" }}
            data-testid="bulk-select-toggle"
          >
            {selected.size === matches.length ? "Clear" : "Select all"}
          </button>
        )}
      </div>

      {selected.size > 0 && (
        <div
          className="mb-3 flex items-center justify-between gap-2 rounded-md p-2.5"
          style={{ backgroundColor: "rgba(107,47,186,0.08)", border: "1px solid rgba(107,47,186,0.2)" }}
          data-testid="bulk-invite-bar"
        >
          <div className="text-xs font-semibold" style={{ color: "var(--avt-purple)" }}>
            {selected.size} selected
          </div>
          <Button
            size="sm"
            onClick={bulkInvite}
            disabled={bulkBusy}
            data-testid="bulk-invite-btn"
            className="btn-primary rounded-full h-7 text-xs"
          >
            {bulkBusy
              ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Sending…</>
              : <><UserPlus className="h-3 w-3 mr-1" /> Invite {selected.size}</>}
          </Button>
        </div>
      )}

      {matches.length === 0 ? (
        <p className="text-xs italic" style={{ color: "var(--ink-soft)" }} data-testid="matches-empty">
          No new suggestions right now. You've already reached out to everyone in your area whose
          categories match this event.
        </p>
      ) : (
        <ul className="space-y-2" data-testid="matches-list">
          {matches.map((m) => (
            <li key={m.id}>
              <MatchCard match={m}
                         active={activeVendorId === m.id}
                         checked={selected.has(m.id)}
                         onToggleCheck={() => toggle(m.id)}
                         onPick={onPick}
                         eventId={eventId}
                         onChanged={onChanged} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function MatchCard({ match, active, checked, onToggleCheck, onPick, eventId, onChanged }) {
  const [inviting, setInviting] = useState(false);
  const invite = async () => {
    if (inviting) return;
    setInviting(true);
    try {
      const { data } = await api.post("/ai/invite-vendor",
        { event_id: eventId, vendor_id: match.id });
      toast.success(data.already_invited
        ? `${match.business_name} was already invited.`
        : `Invited ${match.business_name} — they'll see it in their inbox.`);
      onChanged?.();
    } catch (e) {
      toast.error("Couldn't send invite");
    } finally { setInviting(false); }
  };
  return (
    <div
      className="rounded-md p-3 border transition-colors"
      style={{
        backgroundColor: active ? "var(--sand)" : "var(--paper-warm)",
        borderColor: checked ? "var(--avt-purple)" : "var(--border-warm)",
      }}
      data-testid={`match-card-${match.id}`}
    >
      <div className="flex items-start gap-2">
        {onToggleCheck && (
          <input
            type="checkbox"
            checked={!!checked}
            onChange={onToggleCheck}
            className="mt-1 shrink-0"
            aria-label={`Select ${match.business_name} for bulk invite`}
            data-testid={`match-check-${match.id}`}
            style={{ accentColor: "var(--avt-purple)" }}
          />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="text-sm font-semibold truncate">{match.business_name}</div>
              <div className="text-xs mt-0.5 truncate" style={{ color: "var(--ink-soft)" }}>
                {match.categories.join(" · ")}
              </div>
            </div>
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: "rgba(44,196,217,0.20)", color: "#0e7490" }}
                  title="Category overlap with this event">
              ×{match.match_score}
            </span>
          </div>
          {match.bio && (
            <div className="text-xs mt-2 line-clamp-2" style={{ color: "var(--ink-soft)" }}>
              {match.bio}
            </div>
          )}
          <div className="mt-3 flex flex-wrap gap-1.5">
            <Button size="sm" onClick={() => onPick(match.id)}
                    data-testid={`match-message-${match.id}`}
                    className="rounded-full h-7 text-xs btn-primary">
              <Send className="h-3 w-3 mr-1" /> Message
            </Button>
            <Button size="sm" variant="outline" onClick={invite} disabled={inviting}
                    data-testid={`match-invite-${match.id}`}
                    className="rounded-full h-7 text-xs">
              {inviting
                ? <Loader2 className="h-3 w-3 animate-spin" />
                : <><UserPlus className="h-3 w-3 mr-1" /> Invite</>}
            </Button>
            {match.slug && (
              <Link to={`/vendors/${match.slug}`} target="_blank"
                    className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full underline"
                    style={{ color: "var(--ink-soft)" }}
                    data-testid={`match-view-${match.id}`}>
                <ExternalLink className="h-3 w-3" /> View
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function EmptySelection({ matches }) {
  return (
    <div className="card-warm rounded-2xl p-10 text-center" data-testid="event-messages-empty">
      <MessageSquare className="h-10 w-10 mx-auto" style={{ color: "var(--avt-purple)" }} />
      <h2 className="mt-4 font-serif text-2xl">Pick a vendor to start.</h2>
      <p className="mt-2 text-sm max-w-md mx-auto" style={{ color: "var(--ink-soft)" }}>
        {matches.length > 0
          ? "The left column shows vendors we've matched to your event by area and category. Message them with questions, or one-click invite them to apply."
          : "No suggestions right now — check back after adding more categories, or invite specific vendors directly from their profile."}
      </p>
    </div>
  );
}

function ThreadPane({ eventId, vendorId, vendorSummary, onSent }) {
  const [thread, setThread] = useState(null);
  const [body, setBody] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const endRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/events/${eventId}/threads/${vendorId}`);
      setThread(data);
    } catch (e) {
      setThread({ messages: [], vendor: { id: vendorId, business_name: vendorSummary?.business_name || vendorSummary?.vendor_name || "Vendor" } });
    } finally { setLoading(false); }
  }, [eventId, vendorId, vendorSummary]);

  useEffect(() => { setLoading(true); setAttachments([]); load(); }, [load]);
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [thread?.messages?.length]);

  const canSend = !!(body.trim() || attachments.length > 0);
  const send = async () => {
    if (sending || !canSend) return;
    setSending(true);
    try {
      await api.post(`/events/${eventId}/threads/${vendorId}`,
        { body: body.trim(), attachments });
      setBody("");
      setAttachments([]);
      await load();
      onSent?.();
    } catch (e) {
      toast.error("Couldn't send message");
    } finally { setSending(false); }
  };

  if (loading) return (
    <div className="card-warm rounded-2xl p-10 text-center">
      <Loader2 className="h-6 w-6 mx-auto animate-spin" style={{ color: "var(--avt-purple)" }} />
    </div>
  );

  const vendor = thread?.vendor || {};
  return (
    <div className="card-warm rounded-2xl flex flex-col" style={{ minHeight: 520 }}
         data-testid="event-thread-pane">
      <div className="px-5 py-4 border-b flex items-center justify-between"
           style={{ borderColor: "var(--border-warm)" }}>
        <div>
          <div className="text-sm font-semibold" data-testid="thread-vendor-name">{vendor.business_name}</div>
          {vendor.slug && (
            <Link to={`/vendors/${vendor.slug}`} target="_blank"
                  className="text-xs underline"
                  style={{ color: "var(--ink-soft)" }}
                  data-testid="thread-view-profile">
              View profile <ExternalLink className="h-3 w-3 inline" />
            </Link>
          )}
        </div>
      </div>
      <div className="flex-1 px-5 py-4 space-y-2 overflow-y-auto"
           style={{ maxHeight: 460 }} data-testid="thread-messages">
        {thread.messages.length === 0 ? (
          <p className="text-sm italic text-center py-8" style={{ color: "var(--ink-soft)" }}>
            No messages yet — say hi 👋
          </p>
        ) : thread.messages.map((m) => (
          <Bubble key={m.id} m={m} />
        ))}
        <div ref={endRef} />
      </div>
      <div className="border-t px-5 py-3"
           style={{ borderColor: "var(--border-warm)" }}>
        <div className="flex items-center gap-1 flex-wrap">
          <MessageAttachmentPicker
            attachments={attachments}
            onChange={setAttachments}
            disabled={sending}
            testIdPrefix="organizer-msg-attach"
          />
          <Input
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Ask a question, share details, or paste your terms…"
            disabled={sending}
            className="flex-1"
            data-testid="thread-input"
          />
          <Button onClick={send} disabled={sending || !canSend}
                  className="btn-primary rounded-full"
                  data-testid="thread-send">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" />
                     : <><Send className="h-4 w-4" /></>}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Bubble({ m }) {
  const mine = m.sender_role === "organizer";
  const hasAttachments = !!(m.attachments && m.attachments.length);
  return (
    <div className={`flex flex-col ${mine ? "items-end" : "items-start"}`}
         data-testid={`bubble-${m.sender_role}`}>
      {m.body && (
        <div
          className="max-w-[75%] rounded-2xl px-4 py-2 text-sm whitespace-pre-wrap"
          style={{
            backgroundColor: mine ? "var(--avt-purple)" : "var(--sand)",
            color: mine ? "#fff" : "var(--ink)",
          }}
        >
          {m.body}
        </div>
      )}
      {hasAttachments && (
        <MessageAttachmentList attachments={m.attachments} mine={mine} />
      )}
    </div>
  );
}
